import React, { useState, useRef, useCallback } from 'react';
import { Users2, User, Users, RefreshCw, Trash2, UserPlus, Search, X, Loader2, Check, AlertCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { useAuth } from '../../contexts/AuthContext';
import { isEnabled, isCoOwner, TOURNAMENT_ENTITIES } from '../access';
import { paymentsAPI } from '../../payments/services/PaymentApi';
import { tournamentParticipantAPI, tournamentAPI } from '../services/tournamentApi';
import { apiCall } from '../../services/apiUtil';
import TeamLogo from '../../teams/components/TeamLogo';
import ConfirmModal from '../../components/ConfirmModal';
import PaymentStatusBadge from '../../payments/components/PaymentStatusBadge';
import {
  needsCardPayment, canPayFor, orderDisplayStatus, orderNeedsCard,
} from '../../payments/services/PaymentMethods';
import useStripeCheckout from '../../payments/services/PaymentStripeHooks';

/** Whole dollars stay whole; cents show only when there are any. */
const money = (n) => `$${Number(n).toFixed(2).replace(/\.00$/, '')}`;

/**
 * EnrolledTab Component
 *
 * Displays enrolled tournament participants in three categories:
 * - Teams
 * - Individual Players
 * - Other Registrations (coaches, referees, etc.)
 *
 * When tournament is completed, delete buttons are disabled.
 *
 * @param {Object} props
 * @param {Object} props.tournament - Tournament data
 * @param {Object} props.tournamentVO - Tournament View Object
 * @param {Array} props.participants - Array of participant data
 * @param {number} props.participantsPage - Current page number
 * @param {Object} props.participantsPagination - Pagination metadata
 * @param {Function} props.loadParticipants - Function to reload participants
 * @param {Function} props.setParticipantsPage - Setter for participants page
 * @returns {JSX.Element}
 */
export default function EnrolledTab({
  tournament,
  tournamentVO,
  isOrganizer = false,
  participants = [],
  participantsPage = 1,
  participantsPagination = null,
  participantsSummary = null,
  loadParticipants,
  setParticipantsPage,
}) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Present only inside an academy's own shell — Stripe must return people
  // to the side they started from.
  const { slug: academySlug } = useParams();

  // Local state for remove participant modal
  const [removeParticipant, setRemoveParticipant] = useState({ isOpen: false, participant: null, source: null });
  const [removeLoading, setRemoveLoading] = useState(false);

  // Bulk-delete-all-participants modal state
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Assign role modal state
  const [assignOpen, setAssignOpen] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState({ scorer: false, referee: false, coach: false, coowner: false, parents: false, teamOwner: false });
  const [assigning, setAssigning] = useState(false);
  const searchDebounce = useRef(null);

  const ROLE_OPTIONS = [
    { key: 'scorer',     label: 'Scorer',     color: 'bg-teal-100 text-teal-800' },
    { key: 'referee',    label: 'Referee',    color: 'bg-gray-100 text-gray-800' },
    { key: 'coach',      label: 'Coach',      color: 'bg-blue-100 text-blue-800' },
    { key: 'coowner',    label: 'Organizer',  color: 'bg-purple-100 text-purple-800' },
    { key: 'parents',    label: 'Parent',     color: 'bg-orange-100 text-orange-800' },
    { key: 'teamOwner', label: 'Team Owner', color: 'bg-red-100 text-red-800' },
  ];

  const handleUserSearch = useCallback((q) => {
    setUserQuery(q);
    setSelectedUser(null);
    clearTimeout(searchDebounce.current);
    if (!q.trim()) { setSearchResults([]); return; }
    searchDebounce.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await apiCall(`/user?search=${encodeURIComponent(q)}&limit=8`);
        setSearchResults(res.data || res || []);
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 300);
  }, []);

  const handleAssignRole = async () => {
    if (!selectedUser) { toast.error('Select a user first'); return; }
    if (!Object.values(selectedRoles).some(Boolean)) { toast.error('Select at least one role'); return; }
    setAssigning(true);
    try {
      await tournamentParticipantAPI.register({
        tournamentId: tournament.id,
        userId: selectedUser.id,
        roles: { ...selectedRoles, player: false },
      });
      toast.success(`Roles assigned to ${selectedUser.firstName} ${selectedUser.lastName}`);
      setAssignOpen(false);
      setUserQuery(''); setSearchResults([]); setSelectedUser(null);
      setSelectedRoles({ scorer: false, referee: false, coach: false, coowner: false, parents: false, teamOwner: false });
      loadParticipants(participantsPage);
    } catch (err) {
      toast.error(err.message || 'Failed to assign role');
    } finally { setAssigning(false); }
  };

  const openAssign = () => {
    setUserQuery(''); setSearchResults([]); setSelectedUser(null);
    setSelectedRoles({ scorer: false, referee: false, coach: false, coowner: false, parents: false, teamOwner: false });
    setAssignOpen(true);
  };

  // Handler for removing participant with smart role management
  /** Which row is mid-bill, so a second click cannot raise two charges. */
  const [rebilling, setRebilling] = useState(null);

  /**
   * Charge again for a place whose order was cancelled.
   *
   * The same call the Front Desk makes — a NEW order against the original
   * payer, leaving the cancelled one cancelled so the trail still says what
   * happened. Offered here because this is the screen where the problem is
   * visible, and sending somebody to another screen to act on what they are
   * already looking at is how it gets ignored.
   */
  const rebillEntry = async (participant) => {
    setRebilling(participant.id);
    try {
      await paymentsAPI.rebillOrphanedEntry(participant.lapsedOrder.id);
      toast.success('Charge raised — it can be paid at the front desk now.');
      await loadParticipants?.(participantsPage);
    } catch (err) {
      toast.error(err?.message || 'Could not raise the charge');
    } finally {
      setRebilling(null);
    }
  };

  const handleRemove = async () => {
    const participant = removeParticipant.participant;
    const source = removeParticipant.source;

    if (!participant) return;

    setRemoveLoading(true);
    try {
      // Check what other roles exist
      const hasPlayerRole = participant.player === true;
      const hasOtherRoles = participant.coach || participant.referee || participant.scorer ||
                           participant.coowner || participant.parents || participant.teamOwner;

      if (source === 'player') {
        // Removing from Individual Players (player role)
        if (hasOtherRoles) {
          // Has other roles - just update player to false
          await tournamentParticipantAPI.updateRegistration(participant.id, { player: false });
          toast.success('Player role removed (other roles retained)');
        } else {
          // No other roles - delete the entire record
          await tournamentParticipantAPI.withdrawRegistration(participant.id);
          toast.success('Participant removed successfully');
        }
      } else if (source === 'other') {
        // Removing from Other Registrations (non-player roles)
        if (hasPlayerRole) {
          // Has player role - just update other roles to false
          await tournamentParticipantAPI.updateRegistration(participant.id, {
            coach: false,
            referee: false,
            scorer: false,
            coowner: false,
            parents: false,
            teamOwner: false
          });
          toast.success('Other roles removed (player role retained)');
        } else {
          // No player role - delete the entire record
          await tournamentParticipantAPI.withdrawRegistration(participant.id);
          toast.success('Participant removed successfully');
        }
      } else {
        // Team or unknown source - just delete
        await tournamentParticipantAPI.withdrawRegistration(participant.id);
        toast.success('Participant removed successfully');
      }

      // Reload participants to refresh the list
      loadParticipants(participantsPage);
    } catch (error) {
      console.error('Failed to remove participant:', error);
      toast.error('Failed to remove participant');
    } finally {
      setRemoveLoading(false);
      setRemoveParticipant({ isOpen: false, participant: null, source: null });
    }
  };

  // Handler for bulk-removing every participant at once — only offered
  // before a schedule exists, since the backend rejects it once one does.
  const handleBulkDelete = async () => {
    setBulkDeleteLoading(true);
    try {
      const result = await tournamentAPI.deleteAllParticipants(tournament.id);
      toast.success(`Removed ${result.deleted} participant(s)`);
      loadParticipants(1);
    } catch (error) {
      console.error('Failed to bulk-delete participants:', error);
      toast.error(error.message || 'Failed to delete participants');
    } finally {
      setBulkDeleteLoading(false);
      setBulkDeleteConfirm(false);
    }
  };

  // Use access control to determine if participants can be deleted.
  // isEnabled(PARTICIPANT_REMOVE) is the strict organizer_user_id check, but
  // the backend for individual remove/withdraw (assertSelfOrCanManageParticipant
  // in tournamentParticipantsController.js) already permits co-owners — so add
  // that fallback here too, same as canAssignRole above, instead of leaving a
  // co-owner unable to do something the backend would actually allow.
  const isDeleteEnabled = tournamentVO
    ? (isEnabled(user?.id, TOURNAMENT_ENTITIES.PARTICIPANT_REMOVE, tournamentVO) || isCoOwner(user?.id, tournamentVO))
    : false;

  // Bulk delete stays organizer-only (not extended to co-owners): its backend
  // (deleteAllTournamentParticipants) calls assertIsOrganizer -- the same
  // deliberately-strict, no-co-owner-override check used for deleting the
  // whole tournament -- so showing this button to a co-owner would 403 on
  // click. Loosening it would need a backend change too, not just this gate.
  const canBulkDelete = isOrganizer
    && isDeleteEnabled
    && (tournamentVO?.rounds?.length ?? 0) === 0
    && participants.length > 0;

  // Organizer can assign roles at any tournament stage (including in-progress) —
  // removing a non-player role (co-owner/scorer/referee/coach/parent) is the
  // exact inverse of that action and doesn't touch the competitive bracket the
  // way removing a player/team would, so it gets the same relaxed rule instead
  // of PARTICIPANT_REMOVE's "must not be in progress" restriction below.
  // Co-owners (labeled "Organizer" in the UI, same as the literal
  // organizer_user_id holder — see EnrolledTab's role list) get this too:
  // the "isOrganizer" prop here is the strict organizer_user_id check, which
  // otherwise left every co-owner unable to assign roles to anyone, including
  // the tournament's own creator when they aren't also organizer_user_id.
  const canAssignRole = isOrganizer || isCoOwner(user?.id, tournamentVO);
  // Same backend (assertSelfOrCanManageParticipant) already permits co-owners.
  const canRemoveOtherRole = isOrganizer || isCoOwner(user?.id, tournamentVO);

  /* Payment is only part of the picture when the tournament actually charges.
   *
   * ⚠️ FROM THE TAG. `registrationFee` is the legacy column and nothing charges
   * against it — `PaymentSellablesService.tournament()` prices an entry from
   * `tournaments.price_id`. Reading the column showed an organiser one rate
   * per entry while the ledger below counted another. The column stays as the
   * fallback for a tournament whose fee was never turned into a tag. */
  const entryFee = Number(tournament?.price?.amount ?? tournament?.registrationFee ?? 0);
  const hasFee = entryFee > 0;

  // Totals come from the server, which sums every registration. Adding up the
  // rows on screen would only ever total the ten this page happens to show.
  const billed = Number(participantsSummary?.amountBilled ?? 0);
  const collected = Number(participantsSummary?.amountCollected ?? 0);
  const outstanding = Number(participantsSummary?.amountOutstanding ?? 0);
  // Where the books and the roster disagree. Three plausible-looking totals could
  // hide both of these, which is how two Paid rows, three unbilled ones and an
  // Outstanding of $0 ended up on the same strip.
  const unmatched = Number(participantsSummary?.unmatchedAmount ?? 0);
  const unbilled = Number(participantsSummary?.unbilledEntries ?? 0);
  const unbilledAmount = Number(participantsSummary?.unbilledAmount ?? 0);
  // Confirming money received is the organizer's call. Deliberately not
  // isDeleteEnabled: that allows "self", and a registrant must never be able
  // to mark their own cash as received. The backend enforces the same rule.
  const canTakePayment = isOrganizer || isCoOwner(user?.id, tournamentVO);


  // Resuming a card payment that never completed. Stripe sends people back to
  // this same tab, where the row will have flipped to Paid.
  const { startCheckout, redirecting } = useStripeCheckout();
  const payReturnPath = academySlug
    ? `/academy/${academySlug}/tournament/${tournament?.id}?tab=enrolled`
    : `/tournament/${tournament?.id}?tab=enrolled`;


  /**
   * What this entry owes and whether it has been settled.
   *
   * Reads the ORDER. It used to read the participant's own amount_due and
   * payment_status, which nothing has written since the cutover — so every entry
   * created since showed as unpaid for ever, including ones paid by card, and
   * "Pay now" cheerfully offered to charge them a second time.
   *
   * Pre-cutover rows have no order and still fall back to their frozen columns,
   * which for them is the only record there is.
   */
  const PaymentCell = ({ p }) => {
    // Organiser, scorer, referee and coach are jobs, not entries, so they owe
    // nothing and must show nothing. Falling through to the legacy columns made a
    // non-playing role read "Credit / debit card · Unpaid" — a payment state for a
    // payment that never existed.
    const isEntry = p.player === true || p.teamId != null;
    if (!isEntry) {
      return (
        <td className="py-3 px-4">
          <span className={`text-sm ${theme.textMuted}`} title="No entry fee for this role">—</span>
        </td>
      );
    }

    const order = p.order || null;

    // NOT BILLED is a different fact from UNPAID, and conflating them was a lie
    // on this screen: three teams with no order at all read "Credit / debit card
    // · Unpaid", printed from registration defaults that nothing has written
    // since the cutover. Nothing was ever charged, so nothing is owed — and the
    // totals correctly knew nothing about them, which is why two Paid rows and
    // three "Unpaid" ones sat above an Outstanding of $0.
    //
    // A pre-cutover entry is told apart by having a real frozen amount: those
    // columns are its only record and are shown. A null amount means no charge
    // was ever raised, which is a defect to surface, not a debt to imply.
    const legacyAmount = p.amountDue != null ? Number(p.amountDue) : null;

    /**
     * BILLED, THEN THE CHARGE WAS PULLED — AND THEY ARE STILL IN THE DRAW.
     *
     * Said separately from "Not billed", because they are not the same
     * problem and the fix is not the same either. Never billed is a charge
     * that was never raised. This is a charge that WAS raised and then
     * cancelled or refunded while the entry stayed behind: the place is
     * occupied, the desk has no order to take money against, and the totals
     * correctly exclude it — so the venue is short and nothing on the screen
     * said why. One label for both is how $140 of it went unnoticed.
     */
    if (!order && p.lapsedOrder) {
      const { status, amount, currency } = p.lapsedOrder;
      const shown = `${currency === 'USD' || !currency ? '$' : ''}${Number(amount).toFixed(2).replace(/\.00$/, '')}`;
      return (
        <td className="py-3 px-4">
          <span className="text-xs font-medium text-red-600"
                title={`A ${shown} charge was ${status} but this entry was never removed. `
                     + 'Nothing can be collected against it — either withdraw the entry or raise a new charge.'}>
            {status === 'refunded' ? 'Refunded — still enrolled' : 'Billing cancelled — still enrolled'}
          </span>
          <span className={`block text-[11px] ${theme.textMuted}`}>{shown} not collectable</span>
        </td>
      );
    }

    if (!order && !legacyAmount) {
      return (
        <td className="py-3 px-4">
          <span className="text-xs font-medium text-amber-600"
                title="No entry fee was ever raised for this registration">
            Not billed
          </span>
        </td>
      );
    }

    const amount = order ? order.amount : legacyAmount;
    const status = order ? orderDisplayStatus(order) : p.paymentStatus;
    const method = order ? order.method : p.paymentMethod;

    return (
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          {amount != null && (
            <span className={`text-sm font-semibold ${theme.text} tabular-nums`}>
              {money(amount)}
            </span>
          )}
          <PaymentStatusBadge method={method} status={status} amountDue={amount} />
          {/* A check number is what matches this entry to a bank statement. */}
          {order?.reference && (
            <span className={`text-[11px] ${theme.textMuted}`}>#{order.reference}</span>
          )}
          {/* Legacy only: nothing reaches pending_confirmation any more. Counter
              payments are confirmed on the Front Desk against the order. */}
          {order
            ? orderNeedsCard(order) && canPayFor(p, user?.id) && (
              <button
                // The checkout page, like everywhere else — a total, every
                // method, and somewhere to land if the card is refused.
                onClick={() => navigate(`/checkout?order=${order.id}`
                  + `&from=${encodeURIComponent(payReturnPath)}`
                  + (academySlug ? `&academy=${encodeURIComponent(academySlug)}` : '')
                  + `&title=${encodeURIComponent('Tournament entry')}`)}
                disabled={redirecting}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50 whitespace-nowrap"
                title="Complete this card payment"
              >
                {redirecting ? 'Opening…' : 'Pay now'}
              </button>
            )
            : needsCardPayment(p) && canPayFor(p, user?.id) && (
              <span className={`text-[11px] ${theme.textMuted}`} title="Registered before payments moved to orders">
                pay at the desk
              </span>
            )}
        </div>
      </td>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-xl font-bold ${theme.text}`}>Enrolled Participants</h3>
          <p className={`text-sm ${theme.textMuted} mt-1`}>
            {participants.length} enrolled
          </p>
        </div>
        <div className="flex items-center gap-x-2">
          {canBulkDelete && (
            <button
              onClick={() => setBulkDeleteConfirm(true)}
              className={`flex items-center ${btn.base} ${btn.md} ${theme.buttonDanger}`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete All Participants
            </button>
          )}
          <button
            onClick={() => loadParticipants(participantsPage)}
            className={`p-2 ${theme.backgroundSecondary} rounded-lg ${theme.cardHover} transition-colors`}
            title="Refresh enrolled list"
          >
            <RefreshCw className={`w-5 h-5 ${theme.link}`} />
          </button>
        </div>
      </div>

      {/* Money collected. Only meaningful once the tournament charges, and
          only the organizer's business. */}
      {hasFee && canTakePayment && billed > 0 && (
        <div className={`${theme.backgroundSecondary} rounded-lg p-4`}>
          {/* FIVE FIGURES, EACH SAYING ONE THING.
              This was three totals — Collected, Outstanding, Total fees — that
              each looked reasonable and together hid two problems: money
              collected for entries no longer here, and entries never charged.
              Two paid rows above an Outstanding of $0 was the result.

              "Outstanding" and "Not billed" are deliberately separate. Billed
              money is receivable; an entry that was never charged is not, and
              calling it unpaid is what made the screen lie in the first place.

              "Net collected" rather than revenue: money owed back was never
              earned, and for a card payment the platform collects, part may be
              owed onward to the host — revenue would overstate ownership. */}
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3">
            <div>
              <p className={`text-xs ${theme.textMuted}`}>Collected</p>
              <p className="text-xl font-bold text-green-600 tabular-nums">{money(collected)}</p>
            </div>
            <div>
              <p className={`text-xs ${theme.textMuted}`}>Outstanding</p>
              <p className={`text-xl font-bold tabular-nums ${outstanding > 0 ? 'text-amber-600' : theme.textMuted}`}>
                {money(outstanding)}
              </p>
              <p className={`text-[11px] ${theme.textMuted}`}>billed, not yet paid</p>
            </div>
            <div>
              <p className={`text-xs ${theme.textMuted}`}>Not booked</p>
              <p className={`text-xl font-bold tabular-nums ${unmatched > 0 ? 'text-amber-600' : theme.textMuted}`}>
                {money(unmatched)}
              </p>
              <p className={`text-[11px] ${theme.textMuted}`}>
                {unmatched > 0 ? 'paid, entry gone' : 'all paid money is booked'}
              </p>
            </div>
            <div>
              <p className={`text-xs ${theme.textMuted}`}>Matched</p>
              <p className={`text-xl font-bold ${theme.text} tabular-nums`}>{money(collected - unmatched)}</p>
              <p className={`text-[11px] ${theme.textMuted}`}>for entries still here</p>
            </div>
            <div>
              <p className={`text-xs ${theme.textMuted}`}>Not billed</p>
              <p className={`text-xl font-bold tabular-nums ${unbilled > 0 ? 'text-red-600' : theme.textMuted}`}>
                {unbilled}
              </p>
              <p className={`text-[11px] ${theme.textMuted}`}>
                {unbilled > 0
                  ? `${unbilled === 1 ? 'entry' : 'entries'} never charged · ${money(unbilledAmount)}`
                  : 'every entry charged'}
              </p>
            </div>
            <p className={`text-xs ${theme.textMuted} ml-auto self-center`}>
              ${entryFee} per entry
            </p>
          </div>

          {(unmatched > 0 || unbilled > 0) && (
            <div className="mt-3 pt-3 border-t border-amber-300/60 flex items-start gap-2">
              <AlertCircle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                {unmatched > 0 && <>{money(unmatched)} is paid but has no entry on this list. It may be a refund or credit the payer will use — decide it on the Front Desk, under “Not booked”. </>}
                {unbilled > 0 && <>The {unbilled} unbilled {unbilled === 1 ? 'entry has' : 'entries have'} no charge to collect; they are absent from every figure above.</>}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Teams Table */}
      <div className={`${theme.backgroundSecondary} rounded-lg overflow-hidden`}>
        <div className={`p-4 ${participants.filter(p => p.teamId != null).length > 0 ? `border-b ${theme.border}` : ''}`}>
          <h3 className={`text-lg font-bold ${theme.text} flex items-center`}>
            <Users2 className={`w-5 h-5 mr-2 ${theme.link}`} />
            Teams ({participants.filter(p => p.teamId != null).length})
          </h3>
        </div>
        {participants.filter(p => p.teamId != null).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${theme.border}`}>
                  <th className={`text-center py-3 px-2 font-semibold ${theme.text} text-xs sm:text-sm`}>#</th>
                  <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm`}>Team Name</th>
                  <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm hidden sm:table-cell`}>Registered By</th>
                  <th className={`text-center py-3 px-2 font-semibold ${theme.text} text-xs sm:text-sm`}>Members</th>
                  <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm hidden md:table-cell`}>Sport</th>
                  <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm hidden lg:table-cell`}>Enrolled On</th>
                  {hasFee && (
                    <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm`}>Payment</th>
                  )}
                  <th className={`text-center py-3 px-2 font-semibold ${theme.text} text-xs sm:text-sm`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {participants.filter(p => p.teamId != null).map((p, idx) => (
                  <tr key={p.id} className={`border-b ${theme.border} ${theme.cardHover}`}>
                    <td className="py-3 px-2 text-center">
                      <div className={`font-semibold ${theme.text} text-sm`}>{idx + 1}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-x-3">
                        <TeamLogo
                          teamId={p.teamId}
                          teamLogo={p.team?.teamLogo}
                          teamName={p.team?.teamName || p.teamName}
                          size="w-8 h-8"
                          textSize="text-xs"
                          rounded="rounded-full"
                        />
                        <div className={`font-medium text-sm`}>
                          <span
                            className={`${theme.link} hover:underline cursor-pointer`}
                            onClick={() => navigate(`/teams/${p.teamId}`)}
                          >
                            {p.team?.teamName || p.teamName || `Team ${p.teamId}`}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className={`py-3 px-4 text-sm ${theme.textSecondary} hidden sm:table-cell`}>
                      {p.team?.captainName || '-'}
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {p.team?.memberCount ?? '-'}
                      </span>
                    </td>
                    <td className={`py-3 px-4 hidden md:table-cell`}>
                      {p.team?.sportName ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {p.team.sportName}
                        </span>
                      ) : (
                        <span className={`text-sm ${theme.textMuted}`}>-</span>
                      )}
                    </td>
                    <td className={`py-3 px-4 text-sm ${theme.textSecondary} hidden lg:table-cell`}>
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '-'}
                    </td>
                    {hasFee && <PaymentCell p={p} />}
                    <td className="py-3 px-2 text-center">
                      <button
                        onClick={() => isDeleteEnabled && setRemoveParticipant({ isOpen: true, participant: p, source: 'team' })}
                        disabled={!isDeleteEnabled}
                        className={`transition-colors p-1 rounded ${
                          !isDeleteEnabled
                            ? 'text-gray-400 cursor-not-allowed opacity-50'
                            : 'text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                        }`}
                        title={!isDeleteEnabled ? "Cannot remove - tournament completed" : "Remove"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`px-4 pb-4 text-sm ${theme.textMuted}`}>No teams enrolled</div>
        )}
      </div>

      {/* Individual Players Table - only for singles/singles_doubles tournaments */}
      {['singles', 'singles_doubles'].includes(tournament?.participantsType) && <div className={`${theme.backgroundSecondary} rounded-lg overflow-hidden`}>
        <div className={`p-4 ${participants.filter(p => p.participantId != null && p.teamId == null && p.player).length > 0 ? `border-b ${theme.border}` : ''}`}>
          <h3 className={`text-lg font-bold ${theme.text} flex items-center`}>
            <User className={`w-5 h-5 mr-2 ${theme.link}`} />
            Individual Players ({participants.filter(p => p.participantId != null && p.teamId == null && p.player).length})
          </h3>
        </div>
        {participants.filter(p => p.participantId != null && p.teamId == null && p.player).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${theme.border}`}>
                  <th className={`text-center py-3 px-2 font-semibold ${theme.text} text-xs sm:text-sm`}>#</th>
                  <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm`}>Player</th>
                  <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm hidden sm:table-cell`}>Email</th>
                  <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm hidden lg:table-cell`}>Enrolled On</th>
                  {hasFee && (
                    <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm`}>Payment</th>
                  )}
                  <th className={`text-center py-3 px-2 font-semibold ${theme.text} text-xs sm:text-sm`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {participants.filter(p => p.participantId != null && p.teamId == null && p.player).map((p, idx) => (
                  <tr key={p.id} className={`border-b ${theme.border} ${theme.cardHover}`}>
                    <td className="py-3 px-2 text-center">
                      <div className={`font-semibold ${theme.text} text-sm`}>{idx + 1}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-x-3">
                        <div className={`w-8 h-8 ${theme.gradient} rounded-full flex items-center justify-center`}>
                          <span className="text-white font-semibold text-xs">
                            {(p.participant?.firstName || p.participantName)?.[0]?.toUpperCase() || 'P'}
                          </span>
                        </div>
                        <div>
                          <div className={`font-medium text-sm`}>
                            <span
                              className={`${theme.link} hover:underline cursor-pointer`}
                              onClick={() => navigate(`/player/${p.participant?.userHandle || p.participantId}`)}
                            >
                              {p.participant ? `${p.participant.firstName || ''} ${p.participant.lastName || ''}`.trim() : p.participantName || `Participant ${p.participantId}`}
                            </span>
                          </div>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Player</span>
                        </div>
                      </div>
                    </td>
                    <td className={`py-3 px-4 text-sm ${theme.textSecondary} hidden sm:table-cell`}>
                      {p.participant?.email || '-'}
                    </td>
                    <td className={`py-3 px-4 text-sm ${theme.textSecondary} hidden lg:table-cell`}>
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '-'}
                    </td>
                    {hasFee && <PaymentCell p={p} />}
                    <td className="py-3 px-2 text-center">
                      {/* The place is here and the charge is gone, so the
                          roster offers the fix rather than only the diagnosis.
                          Beside the remove button on purpose: those are the two
                          answers — collect for the place, or take it back — and
                          somebody looking at the list is the person deciding. */}
                      {p.lapsedOrder?.id && (
                        <button
                          onClick={() => rebillEntry(p)}
                          disabled={rebilling === p.id}
                          data-testid={`enrolled-rebill-${p.id}`}
                          className="mr-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                          title="Raise a fresh charge for this entry so it can be paid"
                        >
                          {rebilling === p.id ? 'Billing…' : 'Bill again'}
                        </button>
                      )}
                      <button
                        onClick={() => isDeleteEnabled && setRemoveParticipant({ isOpen: true, participant: p, source: 'player' })}
                        disabled={!isDeleteEnabled}
                        className={`transition-colors p-1 rounded ${
                          !isDeleteEnabled
                            ? 'text-gray-400 cursor-not-allowed opacity-50'
                            : 'text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                        }`}
                        title={!isDeleteEnabled ? "Cannot remove - tournament completed" : "Remove Player Role"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`px-4 pb-4 text-sm ${theme.textMuted}`}>No individual players enrolled</div>
        )}
      </div>}

      {/* Other Individual Registrations Table - shows users with non-player roles */}
      <div className={`${theme.backgroundSecondary} rounded-lg overflow-hidden`}>
        <div className={`p-4 flex items-center justify-between ${participants.filter(p => p.teamId == null && p.participantId != null && (p.coach || p.referee || p.scorer || p.coowner || p.parents || p.teamOwner)).length > 0 ? `border-b ${theme.border}` : ''}`}>
          <h3 className={`text-lg font-bold ${theme.text} flex items-center`}>
            <Users className={`w-5 h-5 mr-2 ${theme.link}`} />
            Other Registrations ({participants.filter(p => p.teamId == null && p.participantId != null && (p.coach || p.referee || p.scorer || p.coowner || p.parents || p.teamOwner)).length})
          </h3>
          {canAssignRole && (
            <button
              onClick={openAssign}
              className={`${btn.base} ${btn.sm} ${theme.buttonPrimary} flex items-center gap-1.5`}
            >
              <UserPlus className="w-3.5 h-3.5" />
              Assign Role
            </button>
          )}
        </div>
        {participants.filter(p => p.teamId == null && p.participantId != null && (p.coach || p.referee || p.scorer || p.coowner || p.parents || p.teamOwner)).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${theme.border}`}>
                  <th className={`text-center py-3 px-2 font-semibold ${theme.text} text-xs sm:text-sm`}>#</th>
                  <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm`}>Name</th>
                  <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm hidden sm:table-cell`}>Email</th>
                  <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm hidden md:table-cell`}>Roles</th>
                  <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm hidden lg:table-cell`}>Enrolled On</th>
                  {hasFee && (
                    <th className={`text-left py-3 px-4 font-semibold ${theme.text} text-xs sm:text-sm`}>Payment</th>
                  )}
                  <th className={`text-center py-3 px-2 font-semibold ${theme.text} text-xs sm:text-sm`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {participants.filter(p => p.teamId == null && p.participantId != null && (p.coach || p.referee || p.scorer || p.coowner || p.parents || p.teamOwner)).map((p, idx) => (
                  <tr key={p.id} className={`border-b ${theme.border} ${theme.cardHover}`}>
                    <td className="py-3 px-2 text-center">
                      <div className={`font-semibold ${theme.text} text-sm`}>{idx + 1}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-x-3">
                        <div className={`w-8 h-8 ${theme.secondary} rounded-full flex items-center justify-center`}>
                          <span className="font-semibold text-xs">
                            {(p.participant?.firstName || p.participantName)?.[0]?.toUpperCase() || 'P'}
                          </span>
                        </div>
                        <div className={`font-medium text-sm`}>
                          {p.participantId ? (
                            <span
                              className={`${theme.link} hover:underline cursor-pointer`}
                              onClick={() => navigate(`/player/${p.participant?.userHandle || p.participantId}`)}
                            >
                              {p.participant ? `${p.participant.firstName || ''} ${p.participant.lastName || ''}`.trim() : p.participantName || `Participant ${p.participantId}`}
                            </span>
                          ) : (
                            <span className={theme.text}>
                              {p.participantName || `Registration #${p.id}`}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className={`py-3 px-4 text-sm ${theme.textSecondary} hidden sm:table-cell`}>
                      {p.participant?.email || '-'}
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {p.coach && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Coach</span>}
                        {p.referee && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Referee</span>}
                        {p.scorer && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">Scorer</span>}
                        {p.coowner && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Organizer</span>}
                        {p.parents && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Parent</span>}
                        {p.teamOwner && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Team Owner</span>}
                        {!p.coach && !p.referee && !p.scorer && !p.coowner && !p.parents && !p.teamOwner && (
                          <span className={`text-sm ${theme.textMuted}`}>{p.participantRole || 'Participant'}</span>
                        )}
                      </div>
                    </td>
                    <td className={`py-3 px-4 text-sm ${theme.textSecondary} hidden lg:table-cell`}>
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '-'}
                    </td>
                    {/* The fee belongs to the registration, not to each role on it.
                        A row that is both a player and a scorer appears in two
                        tables here, so showing payment in both made one $50
                        entry read as $100 owed. The player row is where the
                        entry — and therefore the money — lives. */}
                    {hasFee && (p.player
                      ? <td className={`py-3 px-4 text-xs ${theme.textMuted}`}>shown above</td>
                      : <PaymentCell p={p} />)}
                    <td className="py-3 px-2 text-center">
                      <button
                        onClick={() => canRemoveOtherRole && setRemoveParticipant({ isOpen: true, participant: p, source: 'other' })}
                        disabled={!canRemoveOtherRole}
                        className={`transition-colors p-1 rounded ${
                          !canRemoveOtherRole
                            ? 'text-gray-400 cursor-not-allowed opacity-50'
                            : 'text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                        }`}
                        title={!canRemoveOtherRole ? "Only the organizer can remove roles" : "Remove Other Roles"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`px-4 pb-4 text-sm ${theme.textMuted}`}>No other registrations</div>
        )}
      </div>

      {/* Pagination Controls */}
      {participantsPagination && participantsPagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-x-2 mt-6">
          <button
            onClick={() => setParticipantsPage(prev => Math.max(1, prev - 1))}
            disabled={participantsPage === 1}
            className={`${btn.base} ${btn.sm} ${
              participantsPage === 1 ? theme.buttonGhost : theme.buttonSecondary
            }`}
          >
            Previous
          </button>
          <div className={`text-sm ${theme.textMuted}`}>
            Page {participantsPage} of {participantsPagination.totalPages}
          </div>
          <button
            onClick={() => setParticipantsPage(prev => Math.min(participantsPagination.totalPages, prev + 1))}
            disabled={participantsPage === participantsPagination.totalPages}
            className={`${btn.base} ${btn.sm} ${
              participantsPage === participantsPagination.totalPages ? theme.buttonGhost : theme.buttonSecondary
            }`}
          >
            Next
          </button>
        </div>
      )}

      {/* Assign Role Modal */}
      {assignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setAssignOpen(false)}>
          <div className={`w-full max-w-md rounded-2xl ${theme.card} shadow-xl p-6`} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-500" />
                <h3 className={`text-base font-semibold ${theme.text}`}>Assign Role</h3>
              </div>
              <button onClick={() => setAssignOpen(false)} className={`p-1 rounded-lg ${theme.textMuted} hover:opacity-70`}>
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* User search */}
            <div className="mb-4">
              <label className={`block text-xs font-medium mb-1.5 ${theme.textMuted}`}>Search user</label>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${theme.border} ${theme.background}`}>
                <Search className={`w-4 h-4 flex-shrink-0 ${theme.textMuted}`} />
                <input
                  autoFocus
                  type="text"
                  value={userQuery}
                  onChange={e => handleUserSearch(e.target.value)}
                  placeholder="Name or email..."
                  className={`flex-1 bg-transparent outline-none text-sm ${theme.text} placeholder:${theme.textMuted}`}
                />
                {searchLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500 flex-shrink-0" />}
              </div>

              {/* Results dropdown */}
              {searchResults.length > 0 && !selectedUser && (
                <div className={`mt-1 rounded-lg border ${theme.border} ${theme.card} overflow-hidden shadow-lg`}>
                  {searchResults.map(u => (
                    <button
                      key={u.id}
                      onClick={() => { setSelectedUser(u); setSearchResults([]); setUserQuery(`${u.firstName} ${u.lastName}`); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:${theme.backgroundSecondary} transition-colors`}
                    >
                      <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-white">{u.firstName?.[0]?.toUpperCase() || '?'}</span>
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${theme.text} truncate`}>{u.firstName} {u.lastName}</p>
                        {u.email && <p className={`text-xs ${theme.textMuted} truncate`}>{u.email}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected user chip */}
              {selectedUser && (
                <div className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-lg ${theme.backgroundSecondary}`}>
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-semibold text-white">{selectedUser.firstName?.[0]?.toUpperCase()}</span>
                  </div>
                  <span className={`text-sm font-medium ${theme.text} flex-1`}>{selectedUser.firstName} {selectedUser.lastName}</span>
                  <button onClick={() => { setSelectedUser(null); setUserQuery(''); }} className={`${theme.textMuted} hover:opacity-70`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>

            {/* Role checkboxes */}
            <div className="mb-5">
              <label className={`block text-xs font-medium mb-2 ${theme.textMuted}`}>Roles</label>
              <div className="grid grid-cols-2 gap-2">
                {ROLE_OPTIONS.map(({ key, label, color }) => (
                  <button
                    key={key}
                    onClick={() => setSelectedRoles(prev => ({ ...prev, [key]: !prev[key] }))}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-left ${
                      selectedRoles[key]
                        ? `border-blue-500 ${theme.backgroundSecondary}`
                        : `border-transparent ${theme.backgroundSecondary} opacity-60`
                    }`}
                  >
                    <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${selectedRoles[key] ? 'bg-blue-500' : `border ${theme.border}`}`}>
                      {selectedRoles[key] && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${color}`}>{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={handleAssignRole}
                disabled={assigning || !selectedUser || !Object.values(selectedRoles).some(Boolean)}
                className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center justify-center gap-2 disabled:opacity-50`}
              >
                {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Assign Role
              </button>
              <button onClick={() => setAssignOpen(false)} className={`${btn.base} ${btn.md} ${theme.buttonGhost}`}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Participant Confirmation Modal */}
      <ConfirmModal
        isOpen={removeParticipant.isOpen}
        onClose={() => setRemoveParticipant({ isOpen: false, participant: null, source: null })}
        onConfirm={handleRemove}
        title={removeParticipant.source === 'player' ? 'Remove Player Role' : removeParticipant.source === 'other' ? 'Remove Other Roles' : 'Remove Participant'}
        message={(() => {
          const p = removeParticipant.participant;
          const name = p?.team?.teamName
            || p?.participantName
            || (p?.participant ? `${p.participant.firstName || ''} ${p.participant.lastName || ''}`.trim() : '')
            || 'this participant';

          if (removeParticipant.source === 'player') {
            const hasOtherRoles = p?.coach || p?.referee || p?.scorer || p?.coowner || p?.parents || p?.teamOwner;
            if (hasOtherRoles) {
              return `This will remove the Player role from ${name}. Their other roles (Coach, Referee, etc.) will be retained.`;
            }
            return `Are you sure you want to remove ${name} from the tournament? This action cannot be undone.`;
          }

          if (removeParticipant.source === 'other') {
            const hasPlayerRole = p?.player === true;
            if (hasPlayerRole) {
              return `This will remove the other roles (Coach, Referee, etc.) from ${name}. Their Player registration will be retained.`;
            }
            return `Are you sure you want to remove ${name} from the tournament? This action cannot be undone.`;
          }

          return `Are you sure you want to remove ${name} from the tournament? This action cannot be undone.`;
        })()}
        confirmText="Remove"
        confirmStyle="danger"
        loading={removeLoading}
      />

      {/* Delete All Participants Confirmation Modal */}
      <ConfirmModal
        isOpen={bulkDeleteConfirm}
        onClose={() => setBulkDeleteConfirm(false)}
        onConfirm={handleBulkDelete}
        title="Delete All Participants"
        message={`This will permanently remove all ${participants.length} enrolled participant(s) from this tournament. This cannot be undone.`}
        confirmText="Delete All"
        confirmStyle="danger"
        loading={bulkDeleteLoading}
      />
    </div>
  );
}
